import React, { memo, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _isEqual from 'lodash/isEqual';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { sendImpressions } from 'reaxl-analytics';
import { InventoryListingV2 } from 'reaxl-listing';

import configureListingTiles from '@/utilities/configureListingTiles';
import useInventoryListingProps from '@/utilities/useInventoryListingProps';

import { getSimilarListings } from '@/actions/srp/remoteActions';

import { inventoryImpressions } from '@/analytics/srpAnalyticsHandlers';

import cpoContentDuck from '@/ducks/cpoContentDuck.js';
import queryDuck from '@/ducks/queryDuck';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpSimilarListingsDuck from '@/ducks/srp/srpSimilarListingsDuck.js';
import srpSupplementalDuck from '@/ducks/srp/srpSupplementalDuck.js';

import InventoryTransitionCardContainer from './InventoryTransitionCardContainer';
import SimilarInventoryContainerPlaceholder from './SimilarInventoryContainerPlaceholder';

// actions

function SimilarInventoryContainerComponent({
    searchRadius,
    isLastPage,
}) {

    const query = useSelector(queryDuck.selectors.getDuckState);
    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);
    const similarListings = useSelector(srpSimilarListingsDuck.selectors.getActiveInventory);
    const isLoading = useSelector(srpSimilarListingsDuck.selectors.getResultLoading);
    const initialLoad = useSelector(srpSimilarListingsDuck.selectors.getInitialLoad);
    const certifiedSponsor = useSelector(cpoContentDuck.selectors.selectCertifiedContentSimple);
    const totalSupplementalCount = useSelector(srpSupplementalDuck.selectors.getResultCount);

    const excludedListingIds = useSelector((state) => {
        const activeInventory = srpResultsDuck.selectors.getActiveInventory(state) || [];
        return activeInventory.map((listing) => listing.id);
    }, _isEqual);

    const dispatch = useDispatch();
    const getListingProps = useInventoryListingProps();
    const { brand } = useBrand();

    const {
        brand: [, {
            channel,
            inventory_image_url: inventoryImageUrl, ignorePriority,
            vhrProviders,
            paymentDealerId,
            paymentPartnerId,
        }],
        default_query_params: [, { queryParams: configQueryParams = {} }],
        override_carfax_tile: [isOverrideCarfaxTileEnabled, { partner }],
        remove_carfax_tile: [removeCarfaxTile],
        remove_experian_tile: [removeExperianTile],
        standard_listing_phone_display: [displayPhoneNumber],
        standard_listing_owner_name: [hideOwnerName],
        use_fba_payment_calc: [enableFBAPaymentCalc],
        premium_spotlight: [enablePremiumSpotlight],
    } = useFeatures([
        'brand',
        'default_query_params',
        'override_carfax_tile',
        'remove_carfax_tile',
        'remove_experian_tile',
        'standard_listing_phone_display',
        'standard_listing_owner_name',
        'use_fba_payment_calc',
        'premium_spotlight',
    ]);

    const [fetched, setFetched] = useState(false);

    useEffect(() => {
        dispatch(getSimilarListings({
            channel,
            configQueryParams,
            displayPhoneNumber,
            hideOwnerName,
            excludedListingIds,
            inventoryImageUrl,
            ignorePriority,
            query,
            vhrProviders,
            enableFBAPaymentCalc,
            paymentDealerId,
            paymentPartnerId,
            enablePremiumSpotlight,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    const sendSimilarInventoryImpression = (listings, cmp, listingType) => {
        const listingIds = listings.map((listing) => listing.id);

        if (listingIds.length > 0) {
            sendImpressions({
                name: inventoryImpressions,
                data: {
                    cmp,
                    selectedItemIds: listingIds,
                    pixallData: {
                        listingType,
                    },
                },
            });
        }
    };

    useEffect(() => {
        const highlightSimilarListings = similarListings.filter((listing) => listing.vehicleHighlight?.isVehicleHighlight);
        const normalSimilarListings = similarListings.filter((listing) => !listing.vehicleHighlight?.isVehicleHighlight);

        if (!fetched && similarListings.length > 0) {
            setFetched(true);
            sendSimilarInventoryImpression(highlightSimilarListings, 'similar-listing', 'highlight');
            sendSimilarInventoryImpression(normalSimilarListings, 'sim_v_lstg_', 'sim');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [similarListings]);

    const similarListingsWithTiles = configureListingTiles({
        brand,
        certifiedSponsor,
        items: similarListings,
        isOverrideCarfaxTileEnabled,
        partner,
        tier: 'supplemental',
        removeCarfaxTile,
        removeExperianTile,
    });

    if (isLoading || initialLoad) {
        return <SimilarInventoryContainerPlaceholder />;
    }

    return similarListingsWithTiles.length > 0 && (
        <>
            {/* Only show TransitionCard when there are no supplemental results and regular results are limited */}
            {totalSupplementalCount === 0 && resultCount <= 15 && (
                <InventoryTransitionCardContainer
                    key="invSimilarTransitionCard"
                    searchRadius={searchRadius}
                    isLastPage={isLastPage}
                />
            )}
            {similarListingsWithTiles.map((listing, index) => {
                listing.isSimilarListing = true;
                return (
                    <div
                        className="col-xs-12 col-sm-4 display-flex fade-in"
                        key={`similar_listing_${listing.id}`}
                    >
                        <InventoryListingV2
                            data-cmp="similarInventoryListing"
                            {...getListingProps('similar', listing, index)}
                        />
                    </div>
                );
            })}
        </>
    );
}

export const SimilarInventoryContainer = memo(SimilarInventoryContainerComponent, _isEqual);

export default SimilarInventoryContainer;
